import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Filter, RefreshCw } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import apiClient from '@/api/client'
import { formatDateTimeHuman, cn } from '@/lib/utils'
import type { FiscalAuditEvent } from '@/types'

const PHASE_OPTIONS = [
  { id: 'all', label: 'All phases' },
  { id: 'inline_attempt', label: 'Inline attempt' },
  { id: 'worker_retry', label: 'Worker retry' },
  { id: 'credit_debit_note', label: 'Credit / debit note' },
] as const

const OUTCOME_OPTIONS = [
  { id: 'all', label: 'All outcomes' },
  { id: 'success', label: 'Success' },
  { id: 'failure', label: 'Failure' },
] as const

const WORKFLOW_OPTIONS = [
  { id: 'all', label: 'All workflows' },
  { id: 'PRAL_POS', label: 'PRAL POS' },
  { id: 'FBR_DI', label: 'FBR DI' },
  { id: 'LOCAL_BRIDGE', label: 'Local Bridge' },
] as const

/**
 * FiscalAuditLog renders a paginated, filterable, read-only view of every
 * fiscal_audit_events row (each FBR/PRAL/local-bridge attempt — successful
 * or failed). Compliance-grade visibility complements the orders browser:
 * here you see *every* request the system made, not just the ones that
 * resulted in a synced invoice.
 */
export function FiscalAuditLog() {
  const [phase, setPhase] = useState<string>('all')
  const [outcome, setOutcome] = useState<string>('all')
  const [workflow, setWorkflow] = useState<string>('all')
  const [orderQuery, setOrderQuery] = useState<string>('')

  const auditQuery = useQuery({
    queryKey: ['fiscal-audit-events', phase, outcome, workflow],
    queryFn: () =>
      apiClient.getFiscalAuditEvents({
        phase: phase === 'all' ? undefined : phase,
        outcome: outcome === 'all' ? undefined : outcome,
        workflow: workflow === 'all' ? undefined : workflow,
      }),
    staleTime: 15_000,
  })

  const rows: FiscalAuditEvent[] = useMemo(() => {
    const data = auditQuery.data?.data ?? []
    if (!orderQuery.trim()) return data
    const q = orderQuery.trim().toLowerCase()
    return data.filter(
      (r) =>
        (r.order_number ?? '').toLowerCase().includes(q) ||
        (r.order_id ?? '').toLowerCase().includes(q),
    )
  }, [auditQuery.data, orderQuery])

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-4 h-4" /> Fiscal Audit Log
            </CardTitle>
            <CardDescription>
              Every FBR / PRAL POS request the system has made. Read-only —
              this is your compliance trail for credit notes, sync attempts,
              and rejections.
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => auditQuery.refetch()}
            disabled={auditQuery.isFetching}
          >
            <RefreshCw className={cn('w-4 h-4 mr-2', auditQuery.isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">
              Phase
            </label>
            <Select value={phase} onValueChange={setPhase}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PHASE_OPTIONS.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">
              Outcome
            </label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {OUTCOME_OPTIONS.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">
              Workflow
            </label>
            <Select value={workflow} onValueChange={setWorkflow}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {WORKFLOW_OPTIONS.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">
              Order #
            </label>
            <Input
              value={orderQuery}
              onChange={(e) => setOrderQuery(e.target.value)}
              placeholder="Filter by order number"
            />
          </div>
        </div>

        {auditQuery.isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="ml-2 text-sm text-muted-foreground">Loading audit events…</span>
          </div>
        )}

        {!auditQuery.isLoading && rows.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground border rounded-md">
            No fiscal audit events match the current filters.
          </div>
        )}

        {!auditQuery.isLoading && rows.length > 0 && (
          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Order</th>
                  <th className="text-left px-3 py-2">Workflow</th>
                  <th className="text-left px-3 py-2">Phase</th>
                  <th className="text-left px-3 py-2">Outcome</th>
                  <th className="text-right px-3 py-2">HTTP</th>
                  <th className="text-right px-3 py-2">Latency (ms)</th>
                  <th className="text-left px-3 py-2">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => (
                  <tr key={r.id} className="align-top">
                    <td className="px-3 py-2 tabular-nums whitespace-nowrap">
                      {formatDateTimeHuman(r.created_at)}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      {r.order_number ?? r.order_id?.slice(0, 8) ?? '—'}
                    </td>
                    <td className="px-3 py-2">{r.workflow}</td>
                    <td className="px-3 py-2">{r.phase}</td>
                    <td className="px-3 py-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          'capitalize text-[10px]',
                          r.outcome === 'success' &&
                            'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-200 dark:border-emerald-900/40',
                          r.outcome === 'failure' &&
                            'bg-red-50 text-red-800 border-red-200 dark:bg-red-950 dark:text-red-200 dark:border-red-900/40',
                        )}
                      >
                        {r.outcome}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.http_status ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.latency_ms ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-red-700 dark:text-red-300 max-w-[280px] truncate" title={r.error_code ?? ''}>
                      {r.error_code ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
